import React, { LegacyRef, MutableRefObject } from "react";
import Head from "next/head";
import { Caption } from "@bloomberg/media-ui-article";
import { VideoPlaylistPlayer, VideoTypes } from "@bloomberg/media-ui-av";
import { useFortress } from "@foundation/fortress";

import useVideoFortressPauseTrigger from "~components/VideoPlayer/useVideoFortressPauseTrigger";
import useVideoViewportAutoplay from "~components/VideoPlayer/useVideoViewportAutoplay";
import { VideoPlayerProps } from "~components/VideoPlayer/VideoPlayerProvider";
import { Video } from "~types/types";
import { getFortressState } from "~utils/getFortressState";
import sizeImage, { DEFAULT_IMAGE } from "~utils/sizeImage";

import { getVideoPlayerOptions } from "./getVideoOptions";
import styles from "./styles.module.css";
import { useVideoPlayer } from "./useVideoPlayer";

export type LedeVideoPlayerProps = VideoPlayerProps & {
    video: Video;
    location: string;
    sites?: string[];
    lang?: string;
};

export const getLedeVideoOptions = (props: LedeVideoPlayerProps) => {
    const { video, sites, location, lang = "en" } = props;

    const options = getVideoPlayerOptions(video, location, lang === "ja");

    return {
        ...options,
        id: video.bmmrId,
        additionalSites: sites,
    };
};

export const LedeVideoPlaylistPlayer = React.forwardRef<
    VideoTypes.PlaylistMediaPlayer | null,
    LedeVideoPlayerProps
>(
    (
        props: LedeVideoPlayerProps,
        ref: React.ForwardedRef<VideoTypes.PlaylistMediaPlayer> | null
    ) => {
        const { video, sites, onError } = props;
        const {
            autoplay,
            env,
            options,
            preloadThreshold,
            repeat,
            startFrom,
            startMuted,
            loadSuggestions,
        } = useVideoPlayer();

        const poster = sizeImage(video.thumbnail?.url ?? DEFAULT_IMAGE, 620);

        const playerOptions = {
            ...options,
            ...getLedeVideoOptions(props),
            poster,
        };

        const { bmmrId, caption, credit, url, title } = video;

        const fortressContext = useFortress();

        const {
            intersectionObserverTracker,
            onReady: viewportAutoplayOnReady,
        } = useVideoViewportAutoplay(
            ref as MutableRefObject<VideoTypes.PlaylistMediaPlayer>,
            fortressContext && getFortressState(fortressContext).isBlocked
        );

        useVideoFortressPauseTrigger(
            ref as MutableRefObject<VideoTypes.PlaylistMediaPlayer>,
            fortressContext
        );

        const onReady = (): void => {
            viewportAutoplayOnReady();
        };

        const playlist = [{ id: bmmrId, live: false, poster, title }];

        return (
            <div
                className={styles.VideoVodPlayer}
                data-bmmrid={bmmrId}
                data-url={url}
                data-autoplay={autoplay}
                data-poster={poster}
                data-sites={sites ? sites.join(",") : ""}
                data-foundation
                data-repeat={false}
                data-index={0}
                data-module-ad-player
                data-use-title-bar={false}
                ref={intersectionObserverTracker as LegacyRef<HTMLDivElement>}
            >
                <Head>
                    <link
                        key={`lede-video-player-${bmmrId}-poster-image`}
                        rel="preload"
                        as="image"
                        href={poster}
                    />
                </Head>
                <VideoPlaylistPlayer
                    id={`lede-video-player-${bmmrId}`}
                    name={`lede-video-player-${bmmrId}`}
                    autoplay={autoplay}
                    env={env}
                    foundationLayout
                    loadSuggestions={loadSuggestions}
                    onError={onError}
                    onReady={onReady}
                    options={playerOptions}
                    playerRef={ref}
                    playlist={playlist}
                    preloadThreshold={preloadThreshold}
                    ref={intersectionObserverTracker}
                    repeat={repeat}
                    startFrom={startFrom}
                    startMuted={startMuted}
                />
                <Caption credit={credit} caption={caption} />
            </div>
        );
    }
);

LedeVideoPlaylistPlayer.displayName = "LedeVideoPlaylistPlayer";
